/**
 * AGP — bespoke Alpha Global Prestige storefront (Layer-3 tenant override of
 * `@app/storefront/tenant-landing`). Faithful recreation of the 2026 "AGP Website
 * Redesign" handoff: dark corporate green + gold, Playfair Display over Inter,
 * editorial rather than marketplace.
 *
 * This file is the SEAM home.tsx consumes:
 *   - HAS_BESPOKE_LANDING   — turn the override on
 *   - TenantLanding()       — the Home landing (served at homePath '/')
 *   - renderTenantPage(path)— Product / Company / Testimonials / Policies /
 *                             Contact for their layout pages, else null
 *   - TENANT_ROUTES         — the non-reserved paths App.tsx must mount
 *
 * All pages share <AgpShell/> (markets bar + nav + footer). BLESSED folder use —
 * presentation only, no business logic. Products come from the live catalog.
 *
 * Copy + images resolve through `useSiteContent(AGP_SITE)` — manifest defaults
 * are the handoff copy verbatim, and the admin "Site Pages" tab can override
 * any of it at runtime. Structure/anchors/routes stay code.
 *
 * NOTE: unlike AGP v1, the redesign's HOME CARRIES NO PRODUCT GRID — the client's
 * design sends shoppers to /storefront instead. If products are ever wanted back
 * on the landing, a `useFeaturedPackages()` row drops in below the beverages band.
 */
import { Link } from 'react-router-dom';
import { AGP_SITE } from '@mlm/shared';
import { useSiteContent } from '../../../storefront/lib/site-content';
import { renderLines, renderParasJoined } from '../../../storefront/lib/rich-paras';
import { AgpShell } from './shell';
import { AgpProducts } from './marketplace';
import { AgpCompany } from './about';
import { AgpTestimonials } from './testimonials';
import { AgpPolicies } from './policies';
import { AgpContact } from './contact';

export const HAS_BESPOKE_LANDING = true;

export function TenantLanding(): JSX.Element {
  return (
    <AgpShell active="home">
      <AgpHome />
    </AgpShell>
  );
}

/** Bespoke pages for non-home paths. Routed via TENANT_ROUTES (build-time), so
 * they work regardless of the tenant's stored layout. */
export function renderTenantPage(path: string): JSX.Element | null {
  if (path === '/storefront') return <AgpProducts />;
  if (path === '/about-us') return <AgpCompany />;
  if (path === '/testimonials') return <AgpTestimonials />;
  if (path === '/policies') return <AgpPolicies />;
  if (path === '/contact') return <AgpContact />;
  return null;
}
export const TENANT_ROUTES = ['/about-us', '/testimonials', '/policies', '/contact'];

function AgpHome(): JSX.Element {
  const sc = useSiteContent(AGP_SITE);
  return (
    <>
      {/* 1 — Hero */}
      <section className="agp-hero">
        <div
          className="agp-hero-overlay"
          style={{ backgroundImage: `url('${sc.img('home.hero.image')}')` }}
        />
        <div className="agp-hero-content">
          <span className="agp-hero-eyebrow">{sc.text('home.hero.eyebrow')}</span>
          <h1>{renderLines(sc.text('home.hero.title'))}</h1>
          <p className="agp-hero-sub">{sc.text('home.hero.sub')}</p>
          <div className="agp-actions">
            <Link className="agp-btn agp-btn-primary" to="/storefront">
              {sc.text('home.hero.ctaPrimary')}
            </Link>
            <Link className="agp-btn agp-btn-outline-light" to="/about-us">
              {sc.text('home.hero.ctaSecondary')}
            </Link>
          </div>
        </div>
      </section>

      {/* 2 — Our Story */}
      <div className="agp-split">
        <div className="agp-split-content">
          <div className="agp-label">{sc.text('home.story.label')}</div>
          <div className="agp-rule" />
          <h2 className="agp-title">{renderLines(sc.text('home.story.title'))}</h2>
          <p className="agp-body">{renderParasJoined(sc.paras('home.story.body'))}</p>
          <div className="agp-stats">
            {sc.items('home.story.stats').map((s) => (
              <div key={s.label}>
                <div className="agp-stat-num">{s.num}</div>
                <div className="agp-stat-label">{s.label}</div>
              </div>
            ))}
          </div>
          <div>
            <Link className="agp-btn agp-btn-outline-dark" to="/about-us">
              {sc.text('home.story.cta')}
            </Link>
          </div>
        </div>
        <div
          className="agp-split-img"
          style={{ backgroundImage: `url('${sc.img('home.story.image')}')` }}
        />
      </div>

      {/* 3 — Why AGP */}
      <section className="agp-section">
        <div className="agp-2col-header">
          <div>
            <div className="agp-label">{sc.text('home.pillars.label')}</div>
            <div className="agp-rule" />
            <h2 className="agp-title" style={{ marginBottom: 0 }}>
              {renderLines(sc.text('home.pillars.title'))}
            </h2>
          </div>
          <p className="agp-lead">{sc.text('home.pillars.lead')}</p>
        </div>
        <div className="agp-hairline-grid cols-3">
          {sc.items('home.pillars.items').map((p) => (
            <div key={p.num} className="agp-pillar">
              <div className="agp-pillar-num">{p.num}</div>
              <div className="agp-pillar-title">{p.title}</div>
              <p className="agp-pillar-body">{p.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* 4 — Awards */}
      <div className="agp-awards">
        {sc.items('home.awards.items').map((a) => (
          <div key={a.name} className="agp-award">
            <div className="agp-award-eyebrow">{a.eyebrow}</div>
            <div className="agp-award-name">{a.name}</div>
            <div className="agp-award-source">{a.source}</div>
          </div>
        ))}
      </div>

      {/* 5 — Beverages feature band (handoff flips this one: copy left, image right) */}
      <div className="agp-banner">
        <div className="agp-banner-content">
          <div className="agp-label">{sc.text('home.beverages.label')}</div>
          <div className="agp-rule" />
          <h2 className="agp-title">{renderLines(sc.text('home.beverages.title'))}</h2>
          <p className="agp-banner-body">{sc.text('home.beverages.body')}</p>
          <div>
            <Link className="agp-btn agp-btn-primary" to="/storefront">
              {sc.text('home.beverages.cta')}
            </Link>
          </div>
        </div>
        <div
          className="agp-banner-img"
          style={{ backgroundImage: `url('${sc.img('home.beverages.image')}')` }}
        />
      </div>

      {/* 6 — Core Values */}
      <section className="agp-section tint">
        <div className="agp-band-header">
          <div>
            <div className="agp-label">{sc.text('home.values.label')}</div>
            <div className="agp-rule" />
            <h2 className="agp-title sm" style={{ marginBottom: 0 }}>
              {sc.text('home.values.title')}
            </h2>
          </div>
          <p className="agp-lead" style={{ maxWidth: 380, fontSize: 14 }}>
            {sc.text('home.values.lead')}
          </p>
        </div>
        <div className="agp-values">
          {sc.items('home.values.items').map((v) => (
            <div key={v.num} className="agp-value">
              <div className="agp-value-num">{v.num}</div>
              <div className="agp-value-name">{v.name}</div>
              <p className="agp-value-desc">{v.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* 7 — CTA */}
      <section className="agp-cta">
        <div
          className="agp-cta-bg"
          style={{ backgroundImage: `url('${sc.img('home.cta.image')}')` }}
        />
        <div className="agp-label">{sc.text('home.cta.label')}</div>
        <h2 className="agp-cta-title">{sc.text('home.cta.title')}</h2>
        <p className="agp-cta-sub">{sc.text('home.cta.sub')}</p>
        <div className="agp-actions">
          <Link className="agp-btn agp-btn-primary" to="/signup">
            {sc.text('home.cta.ctaPrimary')}
          </Link>
          <Link className="agp-btn agp-btn-outline-light" to="/storefront">
            {sc.text('home.cta.ctaSecondary')}
          </Link>
        </div>
      </section>
    </>
  );
}
